import { useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { apiErrorDetail } from '@/lib/api/fraud-client'
import { useAdminCreateBlock, useAdminLiftBlock, useFraudAllBlocks } from '@/lib/api/fraud-queries'
import { FRAUD_BLOCK_REASONS, type FraudBlock, type FraudBlockReason } from '@/lib/api/fraud-types'
import { cn } from '@/lib/utils'
import { Banner, Spinner } from '../shared'
import { EMPTY, PILL, ROW, TD, TH, fmtDateTime } from '../format'
import { CardBrand } from '../card-brand'

const FIELD = 'w-full rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-sm'

/**
 * Place a platform-wide block: every event, every organization. Staff only.
 * Targets an attendee (`userId`, or an id typed in) or a card
 * (`instrumentId`, or an instrument id typed in): the physical card by
 * fingerprint, whoever holds it, with the accounts untouched.
 */
export function AccountBlockForm({ userId, instrumentId, cardLabel, onDone }: {
  userId?: number
  instrumentId?: string
  cardLabel?: string
  onDone?: (block: FraudBlock) => void
}) {
  const create = useAdminCreateBlock()
  const [targetId, setTargetId] = useState(userId ? String(userId) : '')
  const [cardId, setCardId] = useState(instrumentId ?? '')
  const cardMode = Boolean(instrumentId) || (!userId && cardId.trim() !== '')
  const [reason, setReason] = useState<FraudBlockReason>('stolen_card')
  const [freeze, setFreeze] = useState(true)
  const [expiresAt, setExpiresAt] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<FraudBlock | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setCreated(null)
    const id = Number(targetId)
    if (!cardMode && !id) {
      setError('Enter the attendee id, or a card instrument id.')
      return
    }
    try {
      const block = await create.mutateAsync({
        ...(cardMode ? { instrument_id: cardId.trim() } : { user: id, freeze_spending: freeze }),
        scope: 'account', reason, notes,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      })
      setCreated(block)
      setNotes('')
      if (!userId) setTargetId('')
      onDone?.(block)
    } catch (err) {
      setError(apiErrorDetail(err, 'Could not create the block.'))
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <Banner kind="error">{error}</Banner>}
      {created && (
        <Banner kind="success">
          Platform-wide block #{created.id} is active{created.kind === 'card' ? ' on the card' : created.user_name ? ` on ${created.user_name}` : ''}.
        </Banner>
      )}
      {instrumentId && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Refuses every charge with <span className="font-medium text-neutral-900 dark:text-white">{cardLabel ?? instrumentId}</span> at every event,
          from any account, and stops it being added. The accounts holding it are untouched.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {!userId && !instrumentId && (
          <>
            <label className="text-sm">
              <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Attendee id</span>
              <Input value={targetId} onChange={(e) => setTargetId(e.target.value)} inputMode="numeric" placeholder="User id (find it on the Attendees tab)" disabled={cardId.trim() !== ''} />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-neutral-700 dark:text-neutral-300">or card instrument id</span>
              <Input value={cardId} onChange={(e) => setCardId(e.target.value)} placeholder="PI… (from an attendee's cards)" />
            </label>
          </>
        )}
        <label className="text-sm">
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Reason</span>
          <select value={reason} onChange={(e) => setReason(e.target.value as FraudBlockReason)} className={FIELD}>
            {FRAUD_BLOCK_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Expires (optional)</span>
          <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </label>
        <label className={cn('text-sm', userId ? '' : 'sm:col-span-2')}>
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Notes</span>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was seen, ticket number, who decided" />
        </label>
      </div>
      {!cardMode && (
        <label className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
          <input type="checkbox" checked={freeze} onChange={(e) => setFreeze(e.target.checked)} className="mt-0.5" />
          <span><span className="font-medium">Freeze spending</span> — also stop orders, transfers and cash or terminal reloads at every event.</span>
        </label>
      )}
      <Button type="submit" variant="destructive" size="sm" disabled={create.isPending}>
        {create.isPending ? 'Blocking…' : cardMode ? 'Block card everywhere' : 'Block account-wide'}
      </Button>
    </form>
  )
}

export function AdminLiftButton({ block }: { block: Pick<FraudBlock, 'id'> }) {
  const lift = useAdminLiftBlock()
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (!open) return <Button variant="outline" size="sm" onClick={() => setOpen(true)}>Lift</Button>
  return (
    <div className="flex flex-col gap-2 min-w-[16rem]">
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why the block is lifted" />
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={lift.isPending}
          onClick={async () => {
            setError(null)
            try {
              await lift.mutateAsync({ blockId: block.id, notes })
              setOpen(false)
            } catch (err) {
              setError(apiErrorDetail(err, 'Could not lift the block.'))
            }
          }}
        >
          {lift.isPending ? 'Lifting…' : 'Confirm lift'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  )
}

export function AdminBlocks() {
  const [activeOnly, setActiveOnly] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const { data, isLoading, error } = useFraudAllBlocks(activeOnly ? { active: true } : undefined)
  const rows = data ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          Active only
        </label>
        <Button size="sm" variant={showForm ? 'outline' : 'default'} onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Close' : 'Block an account'}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader><CardTitle className="text-base">Block an account everywhere</CardTitle></CardHeader>
          <CardContent>
            <AccountBlockForm />
            <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
              Platform-wide blocks apply at every event of every organization, on an attendee or on a card. Event and organization blocks are placed from the event's Risk page.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto">
        {error ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load blocks: {error.message}</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500 dark:text-neutral-400">{activeOnly ? 'No active blocks anywhere.' : 'No blocks recorded.'}</p>
        ) : (
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-800">
                <th className={TH}>Target</th>
                <th className={TH}>Scope</th>
                <th className={TH}>Reason</th>
                <th className={TH}>Freeze</th>
                <th className={TH}>Created</th>
                <th className={TH}>Expires</th>
                <th className={TH}>Status</th>
                <th className={TH}>Notes</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody>
              {rows.map((block) => (
                <tr key={block.id} className={ROW}>
                  <td className={TD}>
                    {block.kind === 'card' ? (
                      <>
                        <CardBrand brand={block.card?.brand} last4={block.card?.last4} wallet={block.card?.wallet} />
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">card, whoever holds it</div>
                      </>
                    ) : (
                      <>
                        {block.user_name || (block.user ? `User #${block.user}` : 'Deleted account')}
                        {block.user && <div className="text-xs text-neutral-500 dark:text-neutral-400">#{block.user}</div>}
                      </>
                    )}
                  </td>
                  <td className={TD}>
                    <span className={cn(PILL, block.scope === 'account' ? 'bg-neutral-800 text-white dark:bg-neutral-200 dark:text-neutral-900' : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>
                      {block.scope}
                    </span>
                    {block.scope === 'event' && block.event && (
                      <div className="text-xs">
                        <Link to="/events/$eventId/fraud" params={{ eventId: String(block.event) }} search={{ tab: 'blocks' }} className="text-blue-600 dark:text-blue-400 hover:underline">
                          {block.event_name ?? `Event #${block.event}`}
                        </Link>
                      </div>
                    )}
                    {block.scope === 'organization' && <div className="text-xs text-neutral-500 dark:text-neutral-400">{block.organization_name ?? EMPTY}</div>}
                  </td>
                  <td className={TD}>{FRAUD_BLOCK_REASONS.find((r) => r.value === block.reason)?.label ?? block.reason}</td>
                  <td className={TD}>{block.kind === 'card' ? 'This card only' : block.freeze_spending ? 'Everything' : 'Cards only'}</td>
                  <td className={TD}>{fmtDateTime(block.created_at)}</td>
                  <td className={TD}>{block.expires_at ? fmtDateTime(block.expires_at) : 'Never'}</td>
                  <td className={TD}>
                    {block.is_active
                      ? <span className={cn(PILL, 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400')}>Active</span>
                      : block.lifted_at
                        ? <span className={cn(PILL, 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400')} title={block.lift_notes}>Lifted {fmtDateTime(block.lifted_at)}</span>
                        : <span className={cn(PILL, 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>Expired</span>}
                  </td>
                  <td className={cn(TD, 'whitespace-normal max-w-[20rem]')}>{block.notes || EMPTY}</td>
                  <td className={TD}>{block.is_active && <AdminLiftButton block={block} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
